'use client';

import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth/types.gen';
import { SvgChevronleft } from '@common/icon';
import { useQuery } from '@tanstack/react-query';

import { EXCEPTED_CODE, UnauthQueryOptions } from '@/api';

import type { FilterOption } from '../../constants';
import { FilterDrawerShell } from '../FilterDrawerShell';
import { FilterSection } from '../FilterSection';
import { FilterSidebarShell } from '../FilterSidebarShell';
import { FilterChipRadioGroup } from './FilterChipRadioGroup';
import { FilterSelectableList } from './FilterSelectableList';

type RegionOption = FilterOption<string>;

interface HometownNewsFilterProps {
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  onApplyDrawer: () => void;
  regionCode: string | null;
  subRegionCode: string | null;
  onRegionChange: (code: string | null) => void;
  onSubRegionChange: (code: string | null) => void;
  onReset: () => void;
}

export function HometownNewsFilter({
  isDrawerOpen,
  onCloseDrawer,
  onApplyDrawer,
  regionCode,
  subRegionCode,
  onRegionChange,
  onSubRegionChange,
  onReset,
}: HometownNewsFilterProps) {
  const { data: regionOptions = [], isLoading: isRegionLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }): RegionOption[] => {
      const contents: ListWelloPolicyCodeAllDto[] = context?.contents ?? [];

      return contents
        .filter(
          (item) =>
            item.code?.startsWith(EXCEPTED_CODE.REGION) &&
            item.level === 2 &&
            !item.code?.endsWith('99'),
        )
        .map((item) => ({
          value: item.code ?? '',
          label: item.value ?? '',
        }))
        .filter((item) => item.value && item.label);
    },
  });

  const { data: subRegionOptions = [], isLoading: isSubRegionLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: Boolean(regionCode),
    select: ({ context }): RegionOption[] => {
      if (!regionCode) return [];

      const contents: ListWelloPolicyCodeAllDto[] = context?.contents ?? [];

      return contents
        .filter(
          (item) =>
            item.level === 3 &&
            item.code?.startsWith(`${regionCode}-`) &&
            !item.code?.endsWith('99'),
        )
        .map((item) => ({
          value: item.code ?? '',
          label: item.value ?? '',
        }))
        .filter((item) => item.value && item.label);
    },
  });
  const selectedRegionLabel = regionOptions.find(
    (item) => item.value === regionCode,
  )?.label;
  const isInSubDepth = Boolean(regionCode && subRegionOptions.length > 0);

  return (
    <>
      <FilterSidebarShell onReset={onReset}>
        {isInSubDepth ? (
          <div className="flex flex-col gap-12">
            <button
              type="button"
              className="flex items-center gap-4 px-12"
              onClick={() => onRegionChange(null)}
            >
              <SvgChevronleft className="size-16 text-Gray-900" />
              <span className="font-700 text-Gray-900 typo-body3">
                {selectedRegionLabel ?? '지역'}
              </span>
            </button>
            <div className="flex flex-col gap-12 px-16">
              <FilterSelectableList
                options={subRegionOptions}
                selectedValue={subRegionCode}
                onSelect={onSubRegionChange}
                allowClear
                isLoading={isSubRegionLoading}
                skeletonCount={20}
              />
            </div>
          </div>
        ) : (
          <FilterSection title="지역">
            <FilterSelectableList
              options={regionOptions}
              selectedValue={regionCode}
              onSelect={onRegionChange}
              allowClear
              isLoading={isRegionLoading}
              skeletonCount={20}
            />
          </FilterSection>
        )}
      </FilterSidebarShell>

      <FilterDrawerShell
        isOpen={isDrawerOpen}
        onReset={onReset}
        onApply={onApplyDrawer}
        onClose={onCloseDrawer}
      >
        {isInSubDepth ? (
          <div className="flex flex-col gap-12">
            <button
              type="button"
              className="flex items-center gap-24"
              onClick={() => onRegionChange(null)}
            >
              <SvgChevronleft className="size-16 text-Gray-900" />
              <span className="font-700 text-Gray-900 typo-body4">
                {selectedRegionLabel ?? '지역'}
              </span>
            </button>
            <FilterChipRadioGroup
              options={subRegionOptions}
              selectedValue={subRegionCode}
              onSelect={onSubRegionChange}
              isLoading={isSubRegionLoading}
              skeletonCount={20}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            <h3 className="font-700 text-Gray-900 typo-body4">지역</h3>
            <FilterChipRadioGroup
              options={regionOptions}
              selectedValue={regionCode}
              onSelect={onRegionChange}
              isLoading={isRegionLoading}
              skeletonCount={20}
            />
          </div>
        )}
      </FilterDrawerShell>
    </>
  );
}
